import type { ReactElement } from 'react';
import { t } from '@fetmode/i18n';

import { OnboardingChoiceCard } from './OnboardingChoiceCard.js';
import {
  TURN_ONS_CATEGORIES,
  type TurnOnsCategoryKey,
  type TurnOnsSelection,
} from './turn-ons-catalog.js';
import './OnboardingFlow.css';
import './TurnOnsPicker.css';

export interface TurnOnsPickerProps {
  value: TurnOnsSelection;
  onChange: (next: TurnOnsSelection) => void;
  disabled?: boolean;
  /** Hide categories already collected earlier in onboarding. */
  omitCategories?: readonly TurnOnsCategoryKey[];
}

function toggleInList<T extends string>(list: readonly T[], item: T): T[] {
  return list.includes(item) ? list.filter((entry) => entry !== item) : [...list, item];
}

export function TurnOnsPicker({
  value,
  onChange,
  disabled = false,
  omitCategories = [],
}: TurnOnsPickerProps): ReactElement {
  const hidden = new Set(omitCategories);

  const updateCategory = (key: TurnOnsCategoryKey, option: string): void => {
    if (key === 'genders') {
      onChange({
        ...value,
        genders: toggleInList(value.genders, option as TurnOnsSelection['genders'][number]),
      });
      return;
    }
    if (key === 'ageGroups') {
      onChange({
        ...value,
        ageGroups: toggleInList(value.ageGroups, option as TurnOnsSelection['ageGroups'][number]),
      });
      return;
    }
    if (key === 'ethnicities') {
      onChange({
        ...value,
        ethnicities: toggleInList(
          value.ethnicities,
          option as TurnOnsSelection['ethnicities'][number],
        ),
      });
      return;
    }
    onChange({
      ...value,
      bodyTypes: toggleInList(value.bodyTypes, option as TurnOnsSelection['bodyTypes'][number]),
    });
  };

  return (
    <div className="turn-ons-picker">
      {TURN_ONS_CATEGORIES.filter((category) => !hidden.has(category.key)).map((category) => {
        const selected = value[category.key] as readonly string[];
        return (
          <section
            key={category.key}
            className="turn-ons-picker__section"
            aria-labelledby={`turn-ons-${category.key}`}
          >
            <h2 id={`turn-ons-${category.key}`} className="onboarding-flow__section-title">
              {t(category.titleKey)}
            </h2>
            <ul className="onboarding-flow__grid onboarding-flow__grid--flush">
              {category.options.map((option) => {
                const isSelected = selected.includes(option);
                return (
                  <li key={option}>
                    <OnboardingChoiceCard
                      imageSeed={`turnon-${category.key}-${option}`}
                      label={t(`${category.optionKeyPrefix}.${option}`)}
                      selected={isSelected}
                      disabled={disabled}
                      onClick={() => {
                        updateCategory(category.key, option);
                      }}
                    />
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
